import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { LaunchOutlinedIcon } from '@getgo/chameleon-icons/react'
import { PopoverComponent } from '@getgo/chameleon-web'
import { Button, IconButton, Popover, Tooltip, Typography } from '@getgo/chameleon-web-react-wrapper'
import { Grid, makeStyles } from '@material-ui/core'
import mixpanel from 'mixpanel-browser'

import GuidedSetupSurveyDialog, { TriggerType } from 'components/shared/GuidedSetupSurveyDialog'
import { DynamicSetupConfig } from 'types/DynamicSetupConfig'
import { PBX } from 'types/Pbx'
import { mixpanelEventNames } from 'utils'
import DynamicSetupDialog from './DynamicSetupDialog'

const useStyles = makeStyles(() => ({
  popoverContainer: {
    paddingTop: tokens.spacing07,
    paddingRight: tokens.spacing04,
    paddingBottom: tokens.spacing04,
    paddingLeft: tokens.spacing04,
  },
}))

export interface DialogSetupProps {
  dynamicSetupConfig: DynamicSetupConfig
  onCompleteSetup: () => void
  pbx: PBX | null
  isDefaultLanguage: boolean
}

const DynamicSetup = ({
  dynamicSetupConfig,
  onCompleteSetup,
  pbx,
  isDefaultLanguage,
}: DialogSetupProps): JSX.Element => {
  const classes = useStyles()
  const t = useTranslate()

  const [setupSurveyState, setSetupSurveyState] = useState<TriggerType>(TriggerType.None)
  const [isDynamicSetupOpen, setIsDynamicSetupOpen] = useState(false)

  const popoverRef = useRef<any>(null)

  useEffect(() => {
    // window.name is set at `isFromLegacyPortal` in com.jive.v5.portal, so we need to block dynamic setup's experience if name isn't empty
    setIsDynamicSetupOpen(dynamicSetupConfig.shownOnLogIn && window.name === '')
  }, [dynamicSetupConfig])

  // This use callback is important.  Dynamic setup will enter a render loop without it.
  const displayPopover = useCallback((isDisplay: boolean) => {
    const popover = popoverRef.current as PopoverComponent

    if (popover) {
      // We manually open/close the popover because the content needs to be displayed when the dynamic setup is closed and not when the IconButton is clicked
      // The best solition would be to manually set isOpen but it's not handle well in Chameleon (triggerOn="manual")
      if (isDisplay) {
        setTimeout(() => {
          popover.open && popover.open()
        }, 0)
      } else {
        popover.close && popover.close()
      }
    }
  }, [])

  // This use callback is important.  Dynamic setup will enter a render loop without it.
  const onToggle = useCallback(() => {
    setIsDynamicSetupOpen(!isDynamicSetupOpen)

    !isDynamicSetupOpen &&
      mixpanel.track(mixpanelEventNames.DYNAMIC_SETUP_DRAWER, {
        Action: 'Toggled dynamic setup drawer',
      })
  }, [isDynamicSetupOpen, setIsDynamicSetupOpen])

  // This use callback is important.  Dynamic setup will enter a render loop without it.
  const onDialogClosed = useCallback(
    (userDismiss: boolean) => {
      onToggle()
      displayPopover(userDismiss)
    },
    [onToggle, displayPopover],
  )

  const onInternalCompleteSetup = useCallback(() => {
    setIsDynamicSetupOpen(false)

    if (isDefaultLanguage) {
      setSetupSurveyState(TriggerType.DynamicSetupComplete)
    } else {
      onCompleteSetup()
    }
  }, [isDefaultLanguage, onCompleteSetup])

  const onFinishSurvey = useCallback(() => {
    setSetupSurveyState(TriggerType.None)
    onCompleteSetup()
  }, [onCompleteSetup])

  return (
    <>
      <IconButton
        id="dynamic-setup-icon"
        data-testid="setup-icon"
        size="medium"
        variant={isDynamicSetupOpen ? 'secondary' : 'primary'}
        onClick={(e) => {
          e.currentTarget.blur()
          onToggle()
          displayPopover(false)
        }}
      >
        <LaunchOutlinedIcon />
      </IconButton>
      <Tooltip triggerId="dynamic-setup-icon">{t('Setup')}</Tooltip>
      <Popover
        data-testId="dynamic-setup-popover"
        triggerId="dynamic-setup-icon"
        position="bottom-end"
        ref={popoverRef}
        arrow
      >
        <Grid
          container
          direction="column"
          alignItems="center"
          spacing={6}
          className={classes.popoverContainer}
          data-testid="helper-modal-popover"
        >
          <Grid item>
            <LaunchOutlinedIcon size="28px" />
          </Grid>
          <Grid item>
            <Typography variant="heading-small">{t('Pick up where you left off')}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body-medium">{t('Continue your setup anytime using the setup launcher.')}</Typography>
          </Grid>
          <Grid item>
            <Button onClick={() => displayPopover(false)}>{t('Got it')}</Button>
          </Grid>
        </Grid>
      </Popover>

      <DynamicSetupDialog
        isDynamicSetupOpen={isDynamicSetupOpen}
        onDialogClosed={onDialogClosed}
        onCompleteSetup={onInternalCompleteSetup}
      />

      <GuidedSetupSurveyDialog onClose={onFinishSurvey} pbx={pbx} triggerType={setupSurveyState} />
    </>
  )
}

export default DynamicSetup
